import { useState } from "react";
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  CalendarDays,
  Landmark,
  MoreVertical,
  Pencil,
  Save,
  Trash2,
  X,
  PiggyBank,
  ReceiptText,
  Target,
  TrendingUp,
  WalletCards,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatarMoeda } from "../utils/money";
import { atualizarMovimentacao, excluirMovimentacao } from "../services/financeService";
import "../styles/pages/dashboard.css";

function calcularInteligenciaFinanceira(resumo, movimentacoes) {
  const saldo = Number(resumo?.saldo || 0);
  const despesas = movimentacoes.filter((m) => m.tipo === "despesa");

  if (despesas.length === 0) {
    return {
      tipo: "ok",
      titulo: "Sem gastos registrados ainda",
      texto: "Cadastre suas despesas para o Invexa prever seu ritmo financeiro.",
      diasRestantes: null,
    };
  }

  const gastosUltimos7Dias = despesas.filter((m) => {
    const data = m.data?.toDate ? m.data.toDate() : null;
    if (!data) return false;

    const diff = Date.now() - data.getTime();
    return diff <= 7 * 24 * 60 * 60 * 1000;
  });

  const baseCalculo = gastosUltimos7Dias.length > 0 ? gastosUltimos7Dias : despesas;
  const totalGastos = baseCalculo.reduce((acc, m) => acc + Number(m.valor || 0), 0);
  const mediaDiaria = totalGastos / 7;

  if (saldo <= 0) {
    return {
      tipo: "risco",
      titulo: "Atenção nas saídas",
      texto: "Seu resultado do mês está no limite. Revise os maiores gastos agora.",
      diasRestantes: 0,
    };
  }

  if (mediaDiaria <= 0) {
    return {
      tipo: "ok",
      titulo: "Mês controlado",
      texto: "Ainda não há gastos suficientes para gerar previsão de esgotamento.",
      diasRestantes: null,
    };
  }

  const diasRestantes = Math.floor(saldo / mediaDiaria);

  if (diasRestantes <= 3) {
    return {
      tipo: "risco",
      titulo: `Dinheiro pode acabar em ${diasRestantes} dia(s)`,
      texto: `Sua média de gastos está em ${formatarMoeda(mediaDiaria)} por dia.`,
      diasRestantes,
    };
  }

  if (diasRestantes <= 7) {
    return {
      tipo: "alerta",
      titulo: `Saldo para cerca de ${diasRestantes} dias`,
      texto: "Se continuar nesse ritmo, reduza os gastos diários.",
      diasRestantes,
    };
  }

  return {
    tipo: "ok",
    titulo: "Mês sob controle",
    texto: `No ritmo atual, seu saldo dura cerca de ${diasRestantes} dias.`,
    diasRestantes,
  };
}

function formatarDataMov(mov, fallback) {
  if (mov?.data?.toDate) {
    return mov.data.toDate().toLocaleDateString("pt-BR");
  }

  if (typeof mov?.data === "string") {
    if (mov.data.includes("-") && mov.data.length >= 10) {
      const [ano, mes, dia] = mov.data.slice(0, 10).split("-");
      if (ano && mes && dia) return `${dia}/${mes}/${ano}`;
    }

    return mov.data;
  }

  if (fallback) {
    const [ano, mes] = fallback.split("-");
    if (ano && mes) return `${mes}/${ano}`;
  }

  return "-";
}

function calcularCategoriaPrincipal(movimentacoes) {
  const despesas = movimentacoes.filter((mov) => mov.tipo === "despesa");

  if (despesas.length === 0) {
    return {
      categoria: "Sem despesas",
      valor: 0,
    };
  }

  const porCategoria = despesas.reduce((acc, mov) => {
    const categoria = mov.categoria || "Sem categoria";
    acc[categoria] = (acc[categoria] || 0) + Number(mov.valor || 0);
    return acc;
  }, {});

  const [categoria, valor] = Object.entries(porCategoria).sort((a, b) => b[1] - a[1])[0];

  return {
    categoria,
    valor,
  };
}

function calcularMaiorGasto(movimentacoes) {
  const despesas = movimentacoes.filter((mov) => mov.tipo === "despesa");

  if (despesas.length === 0) {
    return null;
  }

  return [...despesas].sort((a, b) => Number(b.valor || 0) - Number(a.valor || 0))[0];
}

export default function Dashboard({
  uid,
  usuario,
  resumo,
  movimentacoes,
  mesSelecionado,
}) {
  const [mostrarTudo, setMostrarTudo] = useState(false);
  const [menuAberto, setMenuAberto] = useState(null);
  const [editando, setEditando] = useState(null);
  const [formEdicao, setFormEdicao] = useState({
    tipo: "despesa",
    descricao: "",
    valor: "",
    categoria: "",
    data: "",
    observacao: "",
  });
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const userId = uid || usuario?.uid;

  const listaMovimentacoes = Array.isArray(movimentacoes) ? movimentacoes : [];
  const ultimas = mostrarTudo ? listaMovimentacoes : listaMovimentacoes.slice(0, 3);
  const saldoPositivo = Number(resumo?.saldo || 0) >= 0;
  const inteligencia = calcularInteligenciaFinanceira(resumo || {}, listaMovimentacoes);

  
  

  const percentualSaidas =
  Number(resumo?.entradas || 0) > 0
    ? Math.min(
        (Number(resumo?.saidas || 0) /
          Number(resumo?.entradas || 1)) * 100,
        100
      )
    : 0;
  const economiaMes = Number(resumo?.entradas || 0) - Number(resumo?.saidas || 0);
  const percentualEconomia =
    Number(resumo?.entradas || 0) > 0
      ? Math.round((economiaMes / Number(resumo.entradas || 1)) * 100)
      : 0;

  const categoriaPrincipal = calcularCategoriaPrincipal(listaMovimentacoes);
  const maiorGasto = calcularMaiorGasto(listaMovimentacoes);
  const ultimaMovimentacao = listaMovimentacoes[0];

  function obterDataGrafico(item) {
    const data =
      item?.data?.toDate
        ? item.data.toDate()
        : item?.dataISO
        ? new Date(`${item.dataISO}T12:00:00`)
        : item?.data
        ? new Date(`${item.data}T12:00:00`)
        : null;

    if (!data || Number.isNaN(data.getTime())) return null;

    data.setHours(12, 0, 0, 0);
    return data;
  }

  function chaveDia(data) {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const dia = String(data.getDate()).padStart(2, "0");
    return `${ano}-${mes}-${dia}`;
  }

  const movimentacoesComData = listaMovimentacoes
    .map((item) => ({ item, data: obterDataGrafico(item) }))
    .filter((linha) => linha.data)
    .sort((a, b) => a.data.getTime() - b.data.getTime());

  const saldoPorDiaMapa = movimentacoesComData.reduce((acc, { item, data }) => {
    const chave = chaveDia(data);

    if (!acc[chave]) {
      acc[chave] = {
        chave,
        data,
        dia: String(data.getDate()).padStart(2, "0"),
        entradas: 0,
        saidas: 0,
        saldo: 0,
      };
    }

    const valor = Number(item.valor || 0);

    if (item.tipo === "receita") {
      acc[chave].entradas += valor;
    }

    if (item.tipo === "despesa") {
      acc[chave].saidas += valor;
    }

    return acc;
  }, {});

  const saldoPorDiaData = (() => {
    if (movimentacoesComData.length === 0) {
      return [{ dia: "01", entradas: 0, saidas: 0, saldo: 0 }];
    }

    const inicio = new Date(movimentacoesComData[0].data);
    const fim = new Date(movimentacoesComData[movimentacoesComData.length - 1].data);
    const dias = [];
    const cursor = new Date(inicio);

    cursor.setHours(12, 0, 0, 0);
    fim.setHours(12, 0, 0, 0);

    while (cursor <= fim) {
      const chave = chaveDia(cursor);
      const existente = saldoPorDiaMapa[chave];

      dias.push(
        existente || {
          chave,
          data: new Date(cursor),
          dia: String(cursor.getDate()).padStart(2, "0"),
          entradas: 0,
          saidas: 0,
          saldo: 0,
        }
      );

      cursor.setDate(cursor.getDate() + 1);
    }

    return dias;
  })();

  let saldoAcumuladoGrafico = 0;

  const graficoSaldoMes =
    saldoPorDiaData.length > 0
      ? saldoPorDiaData.map((linha) => {
          saldoAcumuladoGrafico += Number(linha.entradas || 0) - Number(linha.saidas || 0);

          return {
            ...linha,
            saldo: Number(saldoAcumuladoGrafico.toFixed(2)),
          };
        })
      : [{ dia: "01", entradas: 0, saidas: 0, saldo: 0 }];

  const mesLabel = (() => {
    if (!mesSelecionado) return "-";
    const [ano, mes] = mesSelecionado.split("-");
    if (!ano || !mes) return mesSelecionado;

    const data = new Date(Number(ano), Number(mes) - 1, 1);
    return data.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  })();

  function dataInputMov(mov) {
    if (mov?.dataISO) return mov.dataISO;
    if (mov?.data?.toDate) return mov.data.toDate().toISOString().slice(0, 10);
    if (typeof mov?.data === "string" && mov.data.length >= 10) return mov.data.slice(0, 10);
    return new Date().toISOString().slice(0, 10);
  }

  function abrirEditor(mov) {
    setMenuAberto(null);
    setEditando(mov);
    setFormEdicao({
      tipo: mov.tipo || "despesa",
      descricao: mov.descricao || "",
      valor: String(mov.valor || ""),
      categoria: mov.categoria || "",
      data: dataInputMov(mov),
      observacao: mov.observacao || "",
    });
  }

  async function salvarEdicao(event) {
    event.preventDefault();
    if (!userId || !editando?.id) return;

    const valorNormalizado = Number(String(formEdicao.valor).replace(",", "."));
    if (!formEdicao.descricao.trim()) {
      alert("Informe a descrição.");
      return;
    }
    if (!valorNormalizado || valorNormalizado <= 0) {
      alert("Informe um valor válido.");
      return;
    }

    setSalvandoEdicao(true);
    try {
      await atualizarMovimentacao(userId, editando.id, {
        ...formEdicao,
        descricao: formEdicao.descricao.trim(),
        categoria: formEdicao.categoria.trim() || "Sem categoria",
        valor: valorNormalizado,
      });
      setEditando(null);
    } catch (error) {
      console.error(error);
      alert(error?.message || "Não foi possível salvar a edição.");
    } finally {
      setSalvandoEdicao(false);
    }
  }

  async function excluirLancamento(mov) {
    setMenuAberto(null);
    if (!userId || !mov?.id) return;

    const confirmar = window.confirm(`Excluir "${mov.descricao || "lançamento"}"?`);
    if (!confirmar) return;

    try {
      await excluirMovimentacao(userId, mov.id);
    } catch (error) {
      console.error(error);
      alert(error?.message || "Não foi possível excluir o lançamento.");
    }
  }

  return (
    <section className="dash-premium">
      <div className="dash-intro">
        <p>Resumo financeiro do mês selecionado.</p>
      </div>

      <div className="dash-top-grid">
        <DashCard
          type={saldoPositivo ? "green" : "red"}
          icon={<WalletCards size={24} />}
          label="Saldo"
          value={formatarMoeda(resumo?.saldo || 0)}
          caption={saldoPositivo ? "Mês positivo" : "Atenção"}
        />

        <DashCard
          type="green"
          icon={<ArrowUpRight size={24} />}
          label="Entradas"
          value={formatarMoeda(resumo?.entradas || 0)}
          caption="Receitas do mês"
        />

        <DashCard
          type="red"
          icon={<ArrowDownLeft size={24} />}
          label="Saídas"
          value={formatarMoeda(resumo?.saidas || 0)}
          caption="Despesas do mês"
        />

        <DashCard
          type={economiaMes >= 0 ? "blue" : "purple"}
          icon={<PiggyBank size={24} />}
          label="Economia"
          value={`${percentualEconomia}%`}
          caption={economiaMes >= 0 ? "Do que entrou sobrou" : "Gasto acima da entrada"}
        />
      </div>

      <section className="dash-analytics-card">
        <div className="dash-analytics-left">
          <h2>Resultado do mês</h2>

          <strong className={saldoPositivo ? "dash-money-positive" : "dash-money-negative"}>
            {formatarMoeda(resumo?.saldo || 0)}
          </strong>

          <div className="dash-analytics-mini">
            <div>
              <span>Mês selecionado</span>
              <strong>{mesLabel}</strong>
            </div>

            <div>
              <span>Lançamentos</span>
              <strong>{listaMovimentacoes.length}</strong>
            </div>
          </div>
        </div>

        <div className="dash-donut-area">
          <div className="dash-donut">
            <div>
              <strong>{Math.round(percentualSaidas)}%</strong>
              <span>saídas</span>
            </div>
          </div>

          <div className={`dash-warning ${inteligencia.tipo}`}>
            <span>
              <AlertTriangle size={15} />
              {inteligencia.titulo}
            </span>
            <p>{inteligencia.texto}</p>
          </div>
        </div>

        <div className="dash-chart-simple">
          <div className="dash-chart-simple-header">
            <div>
              <span>Saldo acumulado</span>
              <strong>{formatarMoeda(resumo?.saldo || 0)}</strong>
            </div>

            <small>{mesLabel}</small>
          </div>

          <ResponsiveContainer width="100%" height={220}>
            <AreaChart
              data={graficoSaldoMes}
              margin={{ top: 18, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="saldoGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.45} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.03} />
                </linearGradient>
              </defs>

              <CartesianGrid
                stroke="rgba(148,163,184,.12)"
                strokeDasharray="4 4"
                vertical={false}
              />

              <XAxis
                dataKey="dia"
                stroke="#94a3b8"
                tickLine={false}
                axisLine={false}
              />

              <YAxis
                stroke="#94a3b8"
                tickLine={false}
                axisLine={false}
                tickFormatter={(valor) => Number(valor || 0).toLocaleString("pt-BR")}
              />

              <Tooltip
                formatter={(valor) => [formatarMoeda(valor), "Saldo"]}
                labelFormatter={(dia) => `Dia ${dia}`}
                contentStyle={{
                  background: "rgba(2,6,23,.95)",
                  border: "1px solid rgba(148,163,184,.14)",
                  borderRadius: "14px",
                  color: "#fff",
                }}
              />

              <Area
                type="monotone"
                dataKey="saldo"
                stroke="#3b82f6"
                strokeWidth={4}
                fill="url(#saldoGradient)"
                dot={{
                  r: 4,
                  fill: "#3b82f6",
                  stroke: "#0f172a",
                  strokeWidth: 3,
                }}
                activeDot={{ r: 6 }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>

          <div className="dash-chart-simple-footer">
            <div>
              <span>Entradas</span>
              <strong className="income">
                {formatarMoeda(resumo?.entradas || 0)}
              </strong>
            </div>

            <div>
              <span>Saídas</span>
              <strong className="expense">
                {formatarMoeda(resumo?.saidas || 0)}
              </strong>
            </div>
          </div>
        </div>
      </section>

      <section className="dash-insights-grid">
        <InsightCard
          type="red"
          icon={<ReceiptText size={23} />}
          label="Maior gasto"
          value={maiorGasto ? formatarMoeda(maiorGasto.valor) : formatarMoeda(0)}
          caption={maiorGasto?.descricao || "Nenhuma despesa registrada"}
        />

        <InsightCard
          type="purple"
          icon={<Target size={23} />}
          label="Categoria principal"
          value={categoriaPrincipal.categoria}
          caption={`${formatarMoeda(categoriaPrincipal.valor)} consumidos`}
        />

        <InsightCard
          type={inteligencia.tipo === "risco" ? "red" : inteligencia.tipo === "alerta" ? "purple" : "green"}
          icon={<TrendingUp size={23} />}
          label="Previsão financeira"
          value={
            inteligencia.diasRestantes === null
              ? "Em análise"
              : inteligencia.diasRestantes <= 0
              ? "No limite"
              : `${inteligencia.diasRestantes} dias`
          }
          caption={
            inteligencia.diasRestantes === null
              ? "Cadastre mais movimentações"
              : "Estimativa com base nos gastos"
          }
        />

        <InsightCard
          type="blue"
          icon={<Landmark size={23} />}
          label="Última movimentação"
          value={ultimaMovimentacao ? formatarMoeda(ultimaMovimentacao.valor) : formatarMoeda(0)}
          caption={ultimaMovimentacao?.descricao || "Nenhuma movimentação ainda"}
        />
      </section>

      <section className="dash-table-card">
        <div className="dash-table-header">
          <div>
            <h2>Últimos lançamentos</h2>
            <span>{listaMovimentacoes.length} movimento(s)</span>
          </div>

          {listaMovimentacoes.length > 3 && (
            <button type="button" onClick={() => setMostrarTudo((atual) => !atual)}>
              {mostrarTudo ? "Ver menos" : "Ver todos"}
              <ArrowUpRight size={16} />
            </button>
          )}
        </div>

        {ultimas.length === 0 ? (
          <div className="dash-empty">Nenhum lançamento neste mês.</div>
        ) : (
          <div className="dash-table">
            <div className="dash-table-head">
              <span>Descrição</span>
              <span>Categoria</span>
              <span>Tipo</span>
              <span>Data</span>
              <span>Valor</span>
              <span></span>
            </div>

            {ultimas.map((mov) => (
              <article className="dash-table-row" key={mov.id}>
                <div className={mov.tipo === "receita" ? "dash-row-icon income" : "dash-row-icon expense"}>
                  {mov.tipo === "receita" ? "+" : "-"}
                </div>

                <div className="dash-row-main">
                  <strong>{mov.descricao || "Sem descrição"}</strong>
                </div>

                <span className="dash-row-category">{mov.categoria || "Sem categoria"}</span>

                <span className={mov.tipo === "receita" ? "dash-badge income" : "dash-badge expense"}>
                  {mov.tipo === "receita" ? "Receita" : "Despesa"}
                </span>

                <span className="dash-row-date">
                  <CalendarDays size={15} />
                  {formatarDataMov(mov, mesSelecionado)}
                </span>

                <strong className={mov.tipo === "receita" ? "dash-row-money income" : "dash-row-money expense"}>
                  {mov.tipo === "receita" ? "+" : "-"} {formatarMoeda(mov.valor)}
                </strong>

                <div className="dash-row-action-wrap">
                  <button
                    className="dash-row-more"
                    type="button"
                    aria-label="Mais opções"
                    onClick={() => setMenuAberto((atual) => (atual === mov.id ? null : mov.id))}
                  >
                    <MoreVertical size={17} />
                  </button>

                  {menuAberto === mov.id && (
                    <div className="dash-row-menu">
                      <button type="button" onClick={() => abrirEditor(mov)}>
                        <Pencil size={15} />
                        Editar
                      </button>
                      <button type="button" className="danger" onClick={() => excluirLancamento(mov)}>
                        <Trash2 size={15} />
                        Excluir
                      </button>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {editando && (
        <div className="dash-edit-backdrop" onClick={() => setEditando(null)}>
          <form className="dash-edit-modal" onSubmit={salvarEdicao} onClick={(e) => e.stopPropagation()}>
            <div className="dash-edit-header">
              <div>
                <span>Editar lançamento</span>
                <h3>{editando.descricao || "Lançamento"}</h3>
              </div>
              <button type="button" onClick={() => setEditando(null)} aria-label="Fechar">
                <X size={18} />
              </button>
            </div>

            <div className="dash-edit-type">
              <button
                type="button"
                className={formEdicao.tipo === "receita" ? "active income" : ""}
                onClick={() => setFormEdicao((atual) => ({ ...atual, tipo: "receita" }))}
              >
                Receita
              </button>
              <button
                type="button"
                className={formEdicao.tipo === "despesa" ? "active expense" : ""}
                onClick={() => setFormEdicao((atual) => ({ ...atual, tipo: "despesa" }))}
              >
                Despesa
              </button>
            </div>

            <label className="dash-edit-field">
              <span>Descrição</span>
              <input
                value={formEdicao.descricao}
                onChange={(e) => setFormEdicao((atual) => ({ ...atual, descricao: e.target.value }))}
                placeholder="Ex: mercado, venda, aluguel"
              />
            </label>

            <label className="dash-edit-field">
              <span>Valor</span>
              <input
                value={formEdicao.valor}
                onChange={(e) => setFormEdicao((atual) => ({ ...atual, valor: e.target.value }))}
                inputMode="decimal"
                placeholder="Ex: 150,00"
              />
            </label>

            <label className="dash-edit-field">
              <span>Categoria</span>
              <input
                value={formEdicao.categoria}
                onChange={(e) => setFormEdicao((atual) => ({ ...atual, categoria: e.target.value }))}
                placeholder="Ex: Alimentação"
              />
            </label>

            <label className="dash-edit-field">
              <span>Data</span>
              <input
                type="date"
                value={formEdicao.data}
                onChange={(e) => setFormEdicao((atual) => ({ ...atual, data: e.target.value }))}
              />
            </label>

            <label className="dash-edit-field full">
              <span>Observação</span>
              <textarea
                value={formEdicao.observacao}
                onChange={(e) => setFormEdicao((atual) => ({ ...atual, observacao: e.target.value }))}
                placeholder="Opcional"
              />
            </label>

            <button className="dash-edit-save" type="submit" disabled={salvandoEdicao}>
              <Save size={17} />
              {salvandoEdicao ? "Salvando..." : "Salvar alterações"}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}

function DashCard({ type, icon, label, value, caption }) {
  return (
    <article className={`dash-card ${type}`}>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <p>{caption}</p>
      </div>

      <div className="dash-card-icon">{icon}</div>
    </article>
  );
}

function InsightCard({ type, icon, label, value, caption }) {
  return (
    <article className={`dash-insight-card ${type}`}>
      <div className="dash-insight-icon">{icon}</div>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <p>{caption}</p>
      </div>
    </article>
  );
}
